'use client'

import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react'
import {
  SessionZipData,
  getSessionZip,
  setSessionZip as persistSessionZip,
  hasAttemptedGeoIpZip,
  markGeoIpZipAttempted,
} from '@/shared/utilities/session-zip-storage.utility'
import { getUsableZipCode } from '@/shared/utilities/zip-code.utility'
import { getLocationFromIP } from '@/features/geo/actions/geo.actions'
import 'client-only'

type SessionZipContextType = {
  sessionZip: SessionZipData
  setZipCode: (zip: string) => void
}

const SessionZipContext = createContext<SessionZipContextType | undefined>(undefined)

export function SessionZipProvider({ children }: { children: ReactNode }) {
  const [sessionZip, setSessionZipState] = useState<SessionZipData>({ zipCode: '' })

  const setZipCode = useCallback((zip: string) => {
    const trimmed = getUsableZipCode(zip)
    setSessionZipState({ zipCode: trimmed })
    persistSessionZip(trimmed)
  }, [])

  useEffect(() => {
    setSessionZipState({ zipCode: getSessionZip() })
  }, [])

  useEffect(() => {
    // The IP lookup is a session-wide fallback: skip it if a zip is already
    // set, or if we have already attempted it this session (persisted, so it
    // survives page reloads and never re-fires when a prior attempt resolved
    // to nothing usable).
    if (getSessionZip() || hasAttemptedGeoIpZip()) return
    markGeoIpZipAttempted()
    getLocationFromIP().then((result) => {
      // The user (or another surface) may have set a zip while this lookup was
      // in flight — that persists synchronously to sessionStorage, so re-check
      // before applying the IP fallback. Otherwise a slow IP response clobbers
      // a zip the user just typed.
      if (getSessionZip()) return
      const postcode = result.success ? getUsableZipCode(result.data.address?.postcode) : ''
      if (postcode) setZipCode(postcode)
    })
  }, [setZipCode])

  return (
    <SessionZipContext.Provider value={{ sessionZip, setZipCode }}>
      {children}
    </SessionZipContext.Provider>
  )
}

export function useSessionZipCode() {
  const context = useContext(SessionZipContext)
  if (context === undefined) {
    throw new Error('useSessionZipCode must be used within a SessionZipProvider')
  }
  return context
}
